import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [lastFetch, setLastFetch] = useState(null);
  const fetchInProgress = useRef(false);

  // Load dismissed notifications from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("dismissedNotifications");
    if (stored) {
      try {
        setDismissedIds(JSON.parse(stored));
      } catch (e) {
        console.error("Error parsing dismissed notifications:", e);
      }
    }
  }, []);

  // Fetch all alert sources with caching
  const fetchNotifications = useCallback(async (force = false) => {
    // Prevent concurrent fetches
    if (fetchInProgress.current) {
      return;
    }

    // Check cache validity (skip if data is fresh and not forced)
    if (!force && lastFetch && (Date.now() - lastFetch) < CACHE_DURATION) {
      setIsLoading(false);
      return;
    }

    fetchInProgress.current = true;
    
    try {
      // First check if notifications are enabled
      try {
        const configResponse = await axios.get(`${API}/system-config/public`);
        if (!configResponse.data.notifications_enabled) {
          // Notifications disabled - don't run any queries
          setNotifications([]);
          setIsLoading(false);
          setLastFetch(Date.now());
          fetchInProgress.current = false;
          return;
        }
      } catch (error) {
        console.error("Error checking system config:", error);
        // Continue if config check fails (default to enabled)
      }

      const allNotifications = [];

      // 1. Check Fivetran status (paused connectors)
      try {
        const fivetranResponse = await axios.get(`${API}/fivetran/status`);
        const pausedConnectors = fivetranResponse.data.connections?.filter(
          conn => conn.status === "PAUSED"
        ) || [];

        pausedConnectors.forEach(conn => {
          allNotifications.push({
            id: `paused-${conn.connection_id}`,
            type: "critical",
            category: "fivetran",
            title: "Conector de datos pausado",
            message: `El conector "${conn.connection_name}" está pausado. Los datos no se están sincronizando.`,
            connector: conn,
            icon: "pause",
            timestamp: new Date().toISOString()
          });
        });
      } catch (error) {
        console.error("Error checking Fivetran status:", error);
      }

      // 2. Check Private Spaces
      try {
        const spacesResponse = await axios.get(`${API}/spaces/private-alerts`);
        const privateSpaces = spacesResponse.data.private_spaces?.filter(
          space => space.task_count > 0
        ) || [];

        privateSpaces.forEach(space => {
          allNotifications.push({
            id: `private-space-${space.space_id}`,
            type: "critical",
            category: "private-space",
            title: "Espacio privado detectado",
            message: `"${space.space_name}" es privado. ${space.task_count} tarea(s) podrían no sincronizarse correctamente.`,
            space: space,
            icon: "lock",
            timestamp: new Date().toISOString()
          });
        });
      } catch (error) {
        console.error("Error checking private spaces:", error);
      }

      setNotifications(allNotifications);
      setLastFetch(Date.now());
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setIsLoading(false);
    } finally {
      fetchInProgress.current = false;
    }
  }, [lastFetch]);

  // Initial fetch on mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Periodic refresh (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications(true); // Force refresh
    }, CACHE_DURATION);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Dismiss a notification
  const dismissNotification = useCallback((notificationId) => {
    setDismissedIds(prev => {
      const newDismissed = [...prev, notificationId];
      localStorage.setItem("dismissedNotifications", JSON.stringify(newDismissed));
      return newDismissed;
    });
  }, []);

  // Clear all dismissed
  const clearDismissed = useCallback(() => {
    setDismissedIds([]);
    localStorage.removeItem("dismissedNotifications");
  }, []);

  // Force refresh (for manual refresh button)
  const refresh = useCallback(() => {
    fetchNotifications(true);
  }, [fetchNotifications]);

  // Computed values
  const activeNotifications = notifications.filter(n => !dismissedIds.includes(n.id));
  const hasActiveProblems = notifications.length > 0;
  const hasUnread = activeNotifications.length > 0;

  const value = {
    notifications,
    activeNotifications,
    isLoading,
    hasActiveProblems,
    hasUnread,
    dismissedIds,
    dismissNotification,
    clearDismissed,
    refresh
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
